import { Injectable } from '@angular/core';
import { ResourceModel } from '../../../core/models/resources/resource.model';
import { OrganizationService } from '../../services/rest/organization/organization.service';
import { ResourceType } from '../../../core/helpers/Const';
import { ServiceModule } from '../../service.module';
import { firstValueFrom } from "rxjs";
import { FolderService } from "../../services/rest/folder/folder.service";
import { ResourceGroupService } from "../../services/rest/resource-group/resource-group.service";

@Injectable({ providedIn: ServiceModule })
export class OrganizationHierarchyStoreService {
    hierarchy: { [key: string]: { [key: string]: ResourceModel[] } } = {};
    organizations: { [key: string]: ResourceModel } = {};
    private containerRequests = new Map<string, Promise<ResourceModel>>();

    constructor(private _organizationService: OrganizationService, private _folderService: FolderService,
        private _resourceGroupService: ResourceGroupService) {}

    async getOrFetchOrganization(organization: string): Promise<ResourceModel> {
        let organizationResult = this.organizations[organization];
        if (organizationResult) {
            return Promise.resolve(organizationResult);
        }

        return firstValueFrom(this._organizationService.get(organization))
            .then((r) => {
                this.organizations[organization] = r;
                return r;
            });
    }

    async getOrFetchContainer(organization: string, path: string, name: string, containerType: ResourceType
    ): Promise<ResourceModel> {
        const hierarchy = this.hierarchy[organization];
        if (hierarchy?.[path]) {
            const resource = hierarchy[path].find((res) => res.name === name && res.type === containerType);
            if (resource) {
                return resource;
            }
        }

        const key = `${organization}|${path}|${name}|${containerType}`;
        if (this.containerRequests.has(key)) {
            return this.containerRequests.get(key);
        }

        const url = `${path}/${name}`;
        const service = containerType === ResourceType.RESOURCE_GROUP
            ? this._resourceGroupService
            : this._folderService;

        const request = firstValueFrom(service.get(url)).then((r) => {
            this.addResource(organization, r);
            return r;
        }).finally(() => {
            this.containerRequests.delete(key); // Clear cache after error
        });

        this.containerRequests.set(key, request);
        return request;
    }

    addResource(organization: string, item: ResourceModel): void {
        if (!this.hierarchy[organization]) {
            this.hierarchy[organization] = {};
        }
        let hierarchy = this.hierarchy[organization];
        if (!this.isContainerResource(item)) {
            return;
        }
        if (!hierarchy[item.path]) {
            hierarchy[item.path] = [];
        }
        let exists = hierarchy[item.path].some((res) => res.name === item.name && res.type === item.type);
        if (!exists) {
            hierarchy[item.path].push(item);
        }
    }

    updateResource(organization: string, item: ResourceModel): void {
        let hierarchy = this.hierarchy[organization];
        if (!this.isContainerResource(item) || !hierarchy) {
            return;
        }
        if (hierarchy[item.path]) {
            let resource = hierarchy[item.path].find((res) => res.name === item.name && item.type === res.type);
            if (resource) {
                let {rules, customSubresources, ...rest} = item;
                Object.assign(resource, rest);
            }
        }
    }

    removeResource(organization: string, item: ResourceModel): void {
        let hierarchy = this.hierarchy[organization];
        if (!this.isContainerResource(item) || !hierarchy) {
            return;
        }
        if (hierarchy[item.path]) {
            hierarchy[item.path] = hierarchy[item.path].filter(
                (resource) => !(resource.name === item.name && resource.type === item.type)
            );
        }
    }

    isContainerResource(resource: ResourceModel): boolean {
        return resource.type === ResourceType.FOLDER || resource.type === ResourceType.RESOURCE_GROUP;
    }

    fetchContainersByPaths(currentResourceType: ResourceType, orgName: string, resourcePaths: { name: string, path: string }[]): Promise<ResourceModel>[] {
        return resourcePaths.map((item, index, array) => {
            const resourceType = index === array.length - 1 && currentResourceType === ResourceType.RESOURCE_GROUP ?
                ResourceType.RESOURCE_GROUP : ResourceType.FOLDER;
            return new Promise<ResourceModel>((resolve) => {
                this.getOrFetchContainer(orgName, `/${item.path}`, item.name, resourceType).then(resolve)
                    .catch(e => resolve(new ResourceModel({ name: item.name, path: item.path })));
            });
        });
    }
}
